import React from 'react';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Robot from '../../models/robots/robot';
import RobotK900Image from '../../media/k900model.png';
import RobotK700Image from '../../media/k700model.png';
import { useTranslation } from 'react-i18next';
import {
  CleaningIcon,
  DisconnectedIcon,
  ErrorIcon,
  getBatteryIconByPercentage,
  MaintenanceIcon,
  StandbyIcon,
} from '@common/utils/icons/icons';
import { dateToStringFormat, dateToTimeSinceString } from '@common/utils/date';
import useCustomLocalizationEffect from '../../hooks/useCustomLocalizationEffect';
import RobotStatus from '../../models/robots/robot-status';
import CustomLocalizationType from '../../models/enums/custom-localization-type';
import CustomContextMenu from '../misc/CustomContextMenu';
import { ContextMenuOption, ContextMenuState } from '../../models/misc/context-menu';
import RobotVersion from '@common/interfaces/cleaning-map/robot-version';

export const getRobotStatusIcon = (
  t: (value: string) => string,
  localize: (value: string | null, type: CustomLocalizationType) => string | null,
  fontSize: 'small' | 'inherit' | 'large' | 'medium',
  status?: RobotStatus | null,
) => {
  const addLastConnection = (tooltip: string) => {
    return (
      <Stack>
        <Typography variant="inherit">{tooltip}</Typography>
        {status?.lastConnection != null && (
          <Typography variant="inherit">{`${t('table-header-last-connection')} ${dateToStringFormat(
            status.lastConnection,
          )}`}</Typography>
        )}
      </Stack>
    );
  };

  if (status?.state == null || !status?.active) {
    return (
      <Tooltip
        title={addLastConnection(
          status?.state == null
            ? t('info-tooltip-unknown-connection-state')
            : t('info-tooltip-disconnected'),
        )}
      >
        <DisconnectedIcon color="disabled" fontSize={fontSize} />
      </Tooltip>
    );
  }
  if (status?.activeErrors != null && status.activeErrors.length > 0) {
    return (
      <Tooltip
        title={addLastConnection(
          status.activeErrors
            .map((error) => localize(error, CustomLocalizationType.ERROR_CODE))
            .join(', '),
        )}
      >
        <ErrorIcon color="error" fontSize={fontSize} />
      </Tooltip>
    );
  }
  if (status?.activeMaintenances != null && status.activeMaintenances.length > 0) {
    return (
      <Tooltip
        title={addLastConnection(
          status.activeMaintenances
            .map((maintenance) => localize(maintenance, CustomLocalizationType.ERROR_CODE))
            .join(', '),
        )}
      >
        <MaintenanceIcon color="warning" fontSize={fontSize} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={addLastConnection(localize(status.state, CustomLocalizationType.STATE) ?? '')}>
      {localize(status.state, CustomLocalizationType.STATE) ===
        localize('Idle', CustomLocalizationType.STATE) ||
      localize(status.state, CustomLocalizationType.STATE) ===
        localize('Standby', CustomLocalizationType.STATE) ? (
        <StandbyIcon color="action" fontSize={fontSize} />
      ) : (
        <CleaningIcon color="success" fontSize={fontSize} />
      )}
    </Tooltip>
  );
};

const RobotCard: React.FC<{
  robot: Robot;
  onClick: (id: string) => void;
  contextMenuOptions?: ContextMenuOption[];
}> = ({ robot, onClick, contextMenuOptions }) => {
  const { t } = useTranslation();
  const localize = useCustomLocalizationEffect();

  const [contextMenu, setContextMenu] = React.useState<ContextMenuState | null>(null);

  const BatteryIcon = getBatteryIconByPercentage(
    robot.status?.batteryPercentage,
    robot.status?.isCharging,
  );
  const StatusIcon = getRobotStatusIcon(t, localize, 'large', robot.status);

  const openContextMenuHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null ? { id: robot.id, mouseX: event.clientX, mouseY: event.clientY } : null,
    );
  };

  const closeContextMenuHandler = () => {
    setContextMenu(null);
  };

  return (
    <React.Fragment>
      <Card sx={{ width: '100%' }}>
        <CardActionArea onClick={() => onClick(robot.id)} onContextMenu={openContextMenuHandler}>
          <Stack
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              px: 2,
              py: 1,
              position: 'relative',
            })}
          >
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={(theme) => ({
                height: theme.spacing(3),
                position: 'absolute',
                top: theme.spacing(2),
                left: theme.spacing(2),
                right: theme.spacing(2),
              })}
            >
              {
                <Typography alignItems="center" display="flex" variant="body2">
                  {robot.status?.batteryPercentage}
                  {robot.status?.batteryPercentage != null && '%'}
                  <BatteryIcon
                    color={
                      robot.status?.batteryPercentage == null
                        ? 'action'
                        : robot.status.batteryPercentage < 30
                          ? 'error'
                          : robot.status.batteryPercentage < 60
                            ? 'warning'
                            : 'success'
                    }
                    sx={{
                      transform:
                        robot.status?.batteryPercentage != null ? 'rotate(90deg)' : undefined,
                      ml: 1,
                    }}
                  />
                </Typography>
              }
            </Stack>
            <CardMedia
              component="img"
              image={robot.model === RobotVersion.K700 ? RobotK700Image : RobotK900Image}
              alt=""
              sx={{
                maxWidth: '80%',
                ml: 'auto',
                mr: 'auto',
                height: { md: '140px', lg: '160px' },
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="body2"
              color="GrayText"
              sx={(theme) => ({
                position: 'absolute',
                bottom: theme.spacing(1),
                left: theme.spacing(2),
              })}
            >
              {robot.model}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              p: 2,
              borderRadius: '0 0 8px 8px',
              width: '100%',
              overflow: 'hidden',
            })}
          >
            {StatusIcon}
            <Stack flexGrow={1} spacing={0.5} sx={{ overflow: 'hidden', width: '100%' }}>
              <Typography variant="h2">{robot.name}</Typography>
              <Typography variant="body2">
                {robot.serialNumber ??
                  `${t('label-serial-number')} - ${t('HardwareVersioningOption-not-configured')}`}
              </Typography>
              <Typography variant="body2">
                {robot.deploymentDates.organizationName}{' '}
                {robot.status?.roomName != null && ` - ${robot.status.roomName}`}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                mt={0.5}
                spacing={1}
                width="100%"
                overflow="hidden"
              >
                <Typography
                  variant="h3"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  noWrap
                  flexGrow={1}
                >
                  {localize(robot.status?.state ?? '', CustomLocalizationType.STATE)}
                </Typography>
                <Typography variant="body2" color="GrayText" noWrap flexShrink={0}>
                  {robot.status?.lastConnection != null &&
                    dateToTimeSinceString(robot.status.lastConnection)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardActionArea>
      </Card>
      {contextMenuOptions != null && (
        <CustomContextMenu
          contextMenuOptions={contextMenuOptions}
          contextMenuState={contextMenu}
          onClose={closeContextMenuHandler}
        />
      )}
    </React.Fragment>
  );
};

export default RobotCard;
